import { useEffect, useRef, useState, useCallback } from 'react'
import { useAuthStore } from '../stores/authStore'

const WS_URL = import.meta.env.VITE_WS_URL || 'wss://9hjji01y4b.execute-api.us-east-1.amazonaws.com/dev'

export interface WebSocketMessage {
  type: 'generation_start' | 'content_chunk' | 'generation_complete' | 'generation_error' |
        'summary_ready' | 'quiz_ready' | 'mindmap_ready' |
        'summary_failed' | 'quiz_failed' | 'mindmap_failed' | 'processing_started' |
        'course_quiz_ready' | 'course_quiz_failed' |
        'flashcards_ready' | 'flashcards_failed' |
        'chat_ready' | 'chat_failed' | 'chat_start' | 'chat_chunk' | 'chat_complete'
  session_id?: string
  material_id?: string
  materialId?: string
  course_id?: string
  version_id?: string
  content_type?: string
  chunk?: string
  content?: any
  key_concepts?: string[]
  detail_level?: 'standard' | 'comprehensive'
  difficulty?: 'easy' | 'medium' | 'hard'
  question_count?: number
  summary?: string
  quiz?: any
  mindmap?: any
  error?: string
  count?: number
  // Chat streaming (Sprint 1-C)
  messageId?: string
  delta?: string
  citations?: { index: number; source: string; score: number }[]
  chatStatus?: 'indexing' | 'ready' | 'failed' | 'unavailable'
}

interface UseWebSocketOptions {
  onMessage?: (message: WebSocketMessage) => void
  onConnect?: () => void
  onDisconnect?: () => void
  onError?: (error: Event) => void
}

export function useWebSocket(options: UseWebSocketOptions = {}) {
  const { user } = useAuthStore()
  const [isConnected, setIsConnected] = useState(false)
  const [isConnecting, setIsConnecting] = useState(false)
  const wsRef = useRef<WebSocket | null>(null)
  const reconnectTimeoutRef = useRef<number>()
  const reconnectAttemptsRef = useRef(0)
  const maxReconnectAttempts = 5

  const connect = useCallback(() => {
    if (!user?.sub || wsRef.current?.readyState === WebSocket.OPEN) {
      return
    }

    setIsConnecting(true)

    try {
      // Connect with userId as query parameter
      const ws = new WebSocket(`${WS_URL}?userId=${user.sub}`)

      ws.onopen = () => {
        console.log('WebSocket connected')
        setIsConnected(true)
        setIsConnecting(false)
        reconnectAttemptsRef.current = 0
        options.onConnect?.()
      }

      ws.onmessage = (event) => {
        try {
          const message: WebSocketMessage = JSON.parse(event.data)
          options.onMessage?.(message)
        } catch (error) {
          console.error('Error parsing WebSocket message:', error)
        }
      }

      ws.onerror = (error) => {
        console.error('WebSocket error:', error)
        options.onError?.(error)
      }

      ws.onclose = () => {
        console.log('WebSocket disconnected')
        setIsConnected(false)
        setIsConnecting(false)
        wsRef.current = null
        options.onDisconnect?.()

        // Attempt to reconnect
        if (reconnectAttemptsRef.current < maxReconnectAttempts) {
          reconnectAttemptsRef.current++
          const delay = Math.min(1000 * Math.pow(2, reconnectAttemptsRef.current), 30000)
          console.log(`Reconnecting in ${delay}ms (attempt ${reconnectAttemptsRef.current})`)
          
          reconnectTimeoutRef.current = setTimeout(() => {
            connect()
          }, delay)
        }
      }

      wsRef.current = ws
    } catch (error) {
      console.error('Error creating WebSocket:', error)
      setIsConnecting(false)
    }
  }, [user, options])

  const disconnect = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current)
    }
    
    if (wsRef.current) {
      wsRef.current.close()
      wsRef.current = null
    }
    
    setIsConnected(false)
    setIsConnecting(false)
    reconnectAttemptsRef.current = 0
  }, [])

  const sendMessage = useCallback((message: any) => {
    if (wsRef.current?.readyState === WebSocket.OPEN) {
      wsRef.current.send(JSON.stringify(message))
    } else {
      console.warn('WebSocket not connected, cannot send message')
    }
  }, [])

  // Auto-connect when user is available
  useEffect(() => {
    if (user?.sub) {
      connect()
    }

    return () => {
      disconnect()
    }
  }, [user?.sub])

  return {
    isConnected,
    isConnecting,
    connect,
    disconnect,
    sendMessage
  }
}
